import { useEffect } from 'react';
import { SITE_URL, BRAND } from '@/config/site';

const DEFAULT_OG_IMAGE = '/og-image.png';
// Per-page share cards (generated by scripts/make_og_cards.mjs, 2026-10-01).
const OG_BY_PATH = {
    '/': '/og/home.jpg', '/pricing': '/og/pricing.jpg', '/how-it-works': '/og/how-it-works.jpg',
    '/find-automation': '/og/find-automation.jpg', '/inquiry': '/og/inquiry.jpg', '/academy': '/og/academy.jpg',
    '/about': '/og/about.jpg', '/verify': '/og/verify.jpg', '/library': '/og/library.jpg', '/play': '/og/play.jpg',
    '/network': '/og/network.jpg', '/membership': '/og/membership.jpg', '/roadmap': '/og/roadmap.jpg', '/shop': '/og/shop.jpg',
    '/automation-center': '/og-automation-center.jpg',
};

export default function useSeo({ title, description, path, ogImage, type = 'website', noindex = false }) {
    useEffect(() => {
        const fullTitle = title
            ? `${title} · ${BRAND.short}`
            : BRAND.seoTitle;
        document.title = fullTitle;

        const setMeta = (name, content, property = false) => {
            if (!content) return;
            const attr = property ? 'property' : 'name';
            let el = document.querySelector(`meta[${attr}="${name}"]`);
            if (!el) {
                el = document.createElement('meta');
                el.setAttribute(attr, name);
                document.head.appendChild(el);
            }
            el.setAttribute('content', content);
        };

        const desc = description || BRAND.metaDescription;

        // Host guard — every non-production host is noindexed at runtime (Section C).
        const host = typeof window !== 'undefined' ? window.location.hostname : '';
        const indexableHost =
            host === 'syntheticmediainvestigator.com' ||
            host === 'www.syntheticmediainvestigator.com' ||
            host === 'localhost' ||
            host === '127.0.0.1';
        const effectiveNoindex = noindex || !indexableHost;

        // noindex was previously accepted but silently ignored — fixed 2026-06 (SEO Section B)
        const robots = document.querySelector('meta[name="robots"]');
        if (effectiveNoindex) {
            setMeta('robots', 'noindex, nofollow');
        } else if (robots) {
            robots.remove();
        }

        setMeta('description', desc);
        setMeta('og:title', fullTitle, true);
        setMeta('og:description', desc, true);
        setMeta('og:type', type, true);
        setMeta('twitter:card', 'summary_large_image');
        setMeta('twitter:title', fullTitle);
        setMeta('twitter:description', desc);

        // Origin — prefer configured SITE_URL, else runtime window origin
        const origin = SITE_URL || (typeof window !== 'undefined' ? window.location.origin : '');
        if (origin) {
            const url = path ? `${origin}${path}` : (typeof window !== 'undefined' ? window.location.href : origin);
            setMeta('og:url', url, true);

            const img = ogImage || OG_BY_PATH[path] || DEFAULT_OG_IMAGE;
            const absImg = img.startsWith('http') ? img : `${origin}${img}`;
            setMeta('og:image', absImg, true);
            setMeta('twitter:image', absImg);

            // Canonical
            let canonical = document.querySelector('link[rel="canonical"]');
            if (!canonical) {
                canonical = document.createElement('link');
                canonical.setAttribute('rel', 'canonical');
                document.head.appendChild(canonical);
            }
            canonical.setAttribute('href', url);
        }
    }, [title, description, path, ogImage, type, noindex]);
}
